"use client"
import Script from 'next/script';
import Link from 'next/link';
import { track } from '@vercel/analytics';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { classNames } from '@/lib/utils';
import { useLoggedInUser } from '@/hooks/useLoggedInUser';
import { chargebeeFrontendSettingsForSite, chargebeeToUse } from '@shared-libs/utils/helpers';
import { useUserProfile } from '@/utils/react-query-hooks';
import { presetColorThemes } from '@/lib/utils';
import { SetupLayoutContext } from './SetupLayoutContext';
import Pricing, { plans } from '@/components/landing/LandingPricing';
import { Button } from '@/components/presentational/tailwind/Button';
import TiktokSearchField from 'src/components/presentational/tailwind/TikTokSearchField';
import ColorPicker from '@/components/presentational/tailwind/ColorPicker';
import ChargebeePaymentForm from '@/components/presentational/tailwind/ChargebeePaymentForm';
import { LoadingSpinner } from '@/components/presentational/tailwind/LoadingSpinner';
// Icons
import LightningFillIcon from "@public/img/icons/lightning-fill.svg";
import { LockClosedIcon } from '@heroicons/react/20/solid';
import { HiArrowSmRight } from 'react-icons/hi';
import { BiCheckShield } from 'react-icons/bi';
import { FiArrowLeft } from 'react-icons/fi';
// import { motion, AnimatePresence } from 'framer-motion';
// const stepTransitionVariants = {
//   initial: { opacity: 0, y: 50 },
//   enter: { opacity: 1, y: 0 },
//   exit: { opacity: 0, y: -50 },
// };
import '@public/css/pricing.css';
import '@public/css/landing.css';

export function Setup({ userCountry, showChargebeeHostedPaymentForm = true, forceSignupCaptcha = false }) {

  const router = useRouter();
  const pathname = usePathname();
  const isLoggedInSetup = pathname.startsWith('/app/setup');
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [accountColor, setAccountColor] = useState(presetColorThemes[0]);
  const [tiktokProfile, setTikTokProfile] = useState(null);
  const [setupStates, setSetupStates] = useState({
    email: searchParams.get('email') || '',
    password: '',
    tiktokUsername: searchParams.get('username') || '',
    activePeriod: 'Yearly' as "Yearly" | "Monthly",
    selectedPlan: plans[0],
  });
  const step = (searchParams.get('step') && setupStates.tiktokUsername) ? parseInt(searchParams.get('step')) : 0;
  const [progressWidth, setProgressWidth] = useState('0%');
  useEffect(() => {
    if (!isLoggedInSetup) return;
    if (step === 0)
      setSetupStates(prevState => ({ ...prevState, tiktokUsername: searchParams.get('username') || '' }));
    if (step !== parseInt(searchParams.get('step')))
      setSearchParams({ step: step.toString() });
  }, [step]);

  useEffect(() => {
    if (searchParams.get('step') === '1' && !setupStates.tiktokUsername)
      setSearchParams({ step: '0' });
  }, [step, setupStates.tiktokUsername]);

  useEffect(() => {
    const initialWidth = isLoggedInSetup
      ? `${((step + 1) * 25).toString()}%`
      : `${((step + 1) * 33).toString()}%`;

    const timer = setTimeout(() => {
      setProgressWidth(initialWidth);
    }, 200);

    return () => clearTimeout(timer);
  }, [step, isLoggedInSetup]);

  // Determine the Chargebee site to use
  const { data: userProfile, isLoading: isProfileLoading } = isLoggedInSetup ? useUserProfile() : { data: null, isLoading: false };
  const [chargebeeSite, setChargebeeSite] = useState(null);
  useEffect(() => {
    (async () => {
      let siteToSet = null;
      if (isLoggedInSetup && userProfile?.gateway) {
        console.log(`Setting chargebeeSite to ${userProfile.gateway}`);
        siteToSet = userProfile.gateway;
      } else {
        const defaultSite = await chargebeeToUse();
        console.log(`Setting chargebeeSite to ${defaultSite}`);
        siteToSet = defaultSite;
      }
      setChargebeeSite(siteToSet);
    })();
  }, [userProfile, isLoggedInSetup]);

  // const supabaseAuthCookie = Object.keys(Object.fromEntries(document.cookie.split('; ').map(cookie => cookie.split('=')))).find(name => /^sb-.+-auth-token$/.test(name));
  // const isLoggedIn = hasCookie('supabase-auth-token') || hasCookie(supabaseAuthCookie);


  const user = isLoggedInSetup ? useLoggedInUser() : null;
  const [cbInstance, setCbInstance] = useState(null);
  useEffect(() => {
    if (!isLoggedInSetup || !chargebeeSite) return;
    if (typeof window !== 'undefined') {
      if (!cbInstance && window.Chargebee) {
        console.log("Initializing Chargebee instance...");
        setCbInstance(
          window.Chargebee.init({
            ...chargebeeFrontendSettingsForSite(chargebeeSite),
            iframeOnly: true,
          })
        );
        return;
      }
    }
  }, [isLoggedInSetup, window, window.Chargebee, cbInstance, chargebeeSite])

  const openChargebeeCheckout = useCallback((planId: string) => {
    console.log('setupStates', setupStates)
    if (typeof window !== 'undefined' && chargebeeSite) {
      if (!cbInstance) {
        if (window.Chargebee.getInstance()) {
          setCbInstance(window.Chargebee.getInstance())
        } else if (window.Chargebee) {
          setCbInstance(
            window.Chargebee.init({
              ...chargebeeFrontendSettingsForSite(chargebeeSite),
              iframeOnly: true,
            })
          );
        }
        return;
      }

      cbInstance?.openCheckout({
        hostedPage: async () => {

          setIsLoading(true)
          let tolt_referral = typeof window !== 'undefined' && (window as any).tolt_referral ? (window as any).tolt_referral : undefined;
          let queryString = {
            planId,
            username: setupStates.tiktokUsername,
            userId: user?.id,
            email: user?.email,
            ...(user?.user_metadata?.full_name && { name: user?.user_metadata?.full_name }),
            ...(tolt_referral && { tolt_referral }),
            site: chargebeeSite
          }
          let qs = Object.keys(queryString).map(key => `${key}=${encodeURIComponent(queryString[key])}`).join('&');
          console.log('qs', qs)

          let retryCount = 0;
          while (retryCount < 2) {
            try {
              const data = await (await fetch(`/api/chargebee/get-checkout-link?${qs}`)).json()
              return data;
            } catch (error) {
              retryCount++;
              if (retryCount >= 2) {
                queryString["openLink"] = 'true';
                qs = Object.keys(queryString).map(key => `${key}=${encodeURIComponent(queryString[key])}`).join('&');
                window.location.href = `/api/chargebee/get-checkout-link?${qs}`;
                break;
              }
            }
          }
        },
        success(hostedPageId) {
          console.log("Successfully created/updated subscription")
          setIsLoading(false)
          // Call /api/chargebee/finish-checkout
          fetch(`/api/chargebee/finish-checkout?site=${chargebeeSite}&hostedPageId=${hostedPageId}&username=${setupStates.tiktokUsername}&planId=${planId}`)
            .then(response => {
              if (response.status === 307) {
                window.location.href = response.headers.get('Location');
              } else {
                return response.json();
              }
            })
            .then(data => {
              console.log("data: ", data);
              setIsLoading(false)
              // Redirect to the account page
              window.location.href = (`/app`);
            })
            .catch(error => {
              console.error("Error finishing checkout: ", error);
              setIsLoading(false)
            });
        },
        close: () => {
          console.log("checkout new closed");
          setIsLoading(false)
        },
        step(step) {
          console.log("checkout", step);
          setIsLoading(false)
        },
      });
    }
  }, [router, cbInstance, user, setupStates, setupStates.tiktokUsername, chargebeeSite]);




  /*             */
  /* Setup Steps */
  /*             */

  const handleStepOne = useCallback(async () => {

    // If user is already logged in, connect the account to OkGrow, and redirect to the account page
    if (isLoggedInSetup) {
      setIsLoading(true);
      try {
        const response = await fetch('/api/accounts/setup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tiktok_username: setupStates.tiktokUsername,
            theme_color: accountColor.name,
          }),
        });
        const data = await response.json();
        if (data.error) {
          setErrorMessage(data.error);
          setIsLoading(false);
          return false;
        }
        router.push(`/app/account/${data.account_id}?connected=true`);
        return true;
      } catch (error) {
        setErrorMessage('An error occurred while connecting your account. Please try again.');
        setIsLoading(false);
        return false;
      }
    }

    if (setupStates.tiktokUsername.trim() === '' || setupStates.tiktokUsername.length < 3) {
      setErrorMessage('Please enter your TikTok username.');
      return false;
    }

    setIsLoading(true);
    console.log(`Fetching TikTok profile @${setupStates.tiktokUsername}...`);
    try {

      const profile = await fetch(`/api/tiktok/get-profile?username=${setupStates.tiktokUsername}`).then(res => res.json());
      // const profile = await TikTok.fetchTiktokProfile(setupStates.tiktokUsername, false);
      if (profile) {
        console.log(`TikTok profile @${setupStates.tiktokUsername} found!`);
        console.log(profile);
        setTikTokProfile(profile);
        return true;
      } else {
        setErrorMessage(`TikTok account <b>@${setupStates.tiktokUsername}</b> not found. Please check the username and try again.`);
        if (typeof document !== 'undefined') {
          const element = document.getElementById('tiktok-username');
          if (element) setTimeout(() => element.focus(), 500);
        }
        setIsLoading(false);
        return false;
      }
    } catch (error) {
      setErrorMessage(`An error occurred while fetching your TikTok profile. Please check the username or try again later.`);
      setIsLoading(false);
      return false;
    }
  }, [setErrorMessage, setIsLoading, setTikTokProfile, setupStates.tiktokUsername]);

  const handleStepTwo = useCallback(async () => {
    const emailRegex = /^[\w-]+(\.[\w-]+)*(\+[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/;
    if (!isLoggedInSetup) {
      if (setupStates.email.trim() === '' || !emailRegex.test(setupStates.email)) {
        setErrorMessage('Please enter a valid email address.');
        return false;
      }
      if (setupStates.password.trim() === '' || setupStates.password.length < 6) {
        setErrorMessage('Please enter a valid password.');
        return false;
      }

      let cfTurnstileValue: string | null = null;
      if (forceSignupCaptcha) {
        const cfTurnstileInputElement = document.querySelector('input[name="cf-turnstile-response"]') as HTMLInputElement;
        cfTurnstileValue = cfTurnstileInputElement ? cfTurnstileInputElement.value : null;
        if (!cfTurnstileValue) {
          setErrorMessage('Please complete the security check.');
          if (window.turnstile)
            window.turnstile.render('#cf-turnstile');
          return false;
        }
      }

      setIsLoading(true);

      fetch('/api/accounts/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: setupStates.email,
          password: setupStates.password,
          tiktok_username: setupStates.tiktokUsername,
          theme_color: accountColor.name,
          ...(forceSignupCaptcha && cfTurnstileValue ? { "cf-turnstile-response": cfTurnstileValue } : {}),
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.error) {
            setErrorMessage(data.error);
            setIsLoading(false);
            return false;
          }
          router.push(`/app/account/${data.account_id}?connected=true`);
        })
        .catch((error) => {
          setErrorMessage(error);
          setIsLoading(false);
          return false;
        });
    } else
      return true;

  }, [isLoggedInSetup, setupStates, accountColor, router]);

  const setSearchParams = useCallback(async (paramsToUpdate: Record<string, string>) => {
    if (!(typeof window !== 'undefined')) return; // Is not browser
    const params = new URLSearchParams(window.location.search);
    setErrorMessage(null);
    for (const [key, value] of Object.entries(paramsToUpdate)) {
      let success = true;
      if (key === 'step') {
        switch (value) {
          case '0':
            // router.push(isLoggedInSetup ? `/app/setup` : '/setup', { scroll: false });
            success = true;
            break;
          case '1':
            if (isLoggedInSetup) {
              success = await handleStepOne();
              if (success) return; // Don't update URL params if account is connected successfully
            } else {
              success = await handleStepOne();
              setTimeout(() => {
                if (forceSignupCaptcha && window.turnstile)
                  window.turnstile.render('#cf-turnstile');
              }, 2000);
            }
            break;
          case '2':
            success = await handleStepTwo();
            break;
          default:
            break;
        }
      }
      if (!success) {
        return;
      }
      params.set(key, value);
    }
    router.push(`?${params.toString()}`, { scroll: false });
    setIsLoading(false);
  }, [router, handleStepOne, handleStepTwo]);

  const handlePlanSelected = useCallback((plan: any) => {
    setSetupStates(prevState => ({ ...prevState, selectedPlan: plan }));
    //NOTE: Uncomment this to enable Chargebee payment form, instead of Checkout popup
    // setSearchParams({ step: '2' })

    let planId = `plan-${plan.name.toLowerCase()}-${plan.price[setupStates.activePeriod == "Yearly" ? "YearlyTotal" : "Monthly"]}`;
    if (setupStates.activePeriod == "Monthly") {
      planId = `plan-first-month-${plan.firstMonthMonthlyPrice}`;
    }
    console.info("planId: ", planId);
    console.info(`setSelectedPlan: `, plan, setupStates.activePeriod);

    openChargebeeCheckout(planId)
  }, [router, cbInstance, setupStates.activePeriod, setupStates.selectedPlan]);

  const handlePeriodSelected = useCallback((period: any) => {
    setSetupStates(prevState => ({ ...prevState, activePeriod: period }));
  }, [router]);

  const Step1 = useMemo(() => (
    <div className='mt-8 animate-fade-in-up-bounce'>

      <div className="space-y-2 text-center">
        <h1 className={`mt-10 mb-2 text-3xl font-bold text-neutral-900 sm:text-3xl lg:mt-5 xl:mt-5 xl:text-4xl`}>
          Get started for free
        </h1>
        <p className={classNames("mx-auto text-md text-gray-400", isLoggedInSetup ? "max-w-sm" : "max-w-md")}>
          Connect your TikTok account to OkGrow and start your growth.
        </p>
      </div>

      <div className="relative mt-8 mb-4">
        <label className="block font-medium text-gray-700 text-sm mb-1.5" htmlFor="tiktok-username">Your TikTok Username</label>
        <TiktokSearchField
          // @ts-ignore:next-line
          id="tiktok-username"
          disableSearch={true}
          disabled={isLoading}
          defaultValue={setupStates.tiktokUsername}
          onValueChange={(value) => {
            setSetupStates(prevState => ({ ...prevState, tiktokUsername: value }))
          }}
          onEnterKeyPressed={() => {
            setSearchParams({ step: '1' })
          }} />

        <div className="flex items-center mt-4 text-sm text-gray-400">
          {/* <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5 mr-2 text-gray-500">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg> */}
          <BiCheckShield className="w-5 h-5 mr-1 text-green-400" />
          <span>We’ll never ask for your TikTok password</span>
        </div>

        <ColorPicker className={`${isLoggedInSetup ? 'mt-5' : 'mt-7 text-sm'} mb-9`} isOnSetupPage={true} onColorChange={(color) => setAccountColor(color)} disabled={isLoading} />
      </div>
      {isLoading ? (
        <Button type="submit" disabled className="!mt-5 flex items-center justify-center w-full px-4 py-3.5 text-md font-medium text-black bg-yellow-300 border border-transparent shadow-sm cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 rounded-full">
          <LoadingSpinner className="w-4 h-4 mr-1" />
          Loading...
        </Button>
      ) : (
          <Button type="submit" className={classNames("!mt-5 flex justify-center w-full px-4 py-3.5 text-md font-medium text-white border border-transparent shadow-md hover:shadow-lg focus:outline-none focus:ring-2 relative overflow-hidden rounded-full",
          accountColor.bgColor, accountColor.hoverColor, accountColor.ringColor
        )}
          onClick={() => {
            setSearchParams({ step: '1' })
            // Track event
            // track('Started Setup', {
            //   username: setupStates.tiktokUsername,
            //   color: accountColor.name,
            // })
          }}>
          Start your growth
          <HiArrowSmRight className="w-5 h-5 place-self-center" />
        </Button>
      )}
      <span className="inline-flex items-center w-full mt-5 font-medium tracking-tight text-green-500 align-middle md:mt-6 place-content-center">
        <LockClosedIcon className="mr-1 h-[0.85rem] w-[0.85rem]" />
        <span>SSL Encrypted</span>
      </span>
    </div>
  ), [router, setupStates.tiktokUsername, setupStates.email, accountColor, isLoading, step]);

  const Step2 = useMemo(() => (
    isLoggedInSetup ? (
      <div className='!mt-0'>
        <Pricing isSetupProcess={true} selectedPeriod={setupStates.activePeriod} />
      </div >
    ) : (
      <div className='relative animate-fade-in-up-bounce'>

          <div className="space-y-2 text-center">
          {!isLoggedInSetup && (
              <Link title="Back" className="flex items-center gap-2 pb-2 group"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                setSearchParams({ step: (step - 1).toString() })
              }}
            >
                <FiArrowLeft className="w-5 h-5 transition-colors duration-200 ease-in-out text-neutral-400 group-hover:text-neutral-500" />
                <span className="transition-colors duration-200 ease-in-out text-neutral-400 group-hover:text-neutral-500">Back</span>
            </Link>
          )}

            <h1 className={`mt-10 mb-2 text-3xl font-bold text-neutral-900 sm:text-3xl lg:mt-5 xl:mt-5 xl:text-4xl`}>
            {(isLoggedInSetup ? "Start your growth for free!" : "Create your account")}
          </h1>
            <p className={classNames("mx-auto text-md text-gray-600 !leading-[20px]", isLoggedInSetup ? "max-w-sm" : "max-w-md")}>
            {(isLoggedInSetup ?
              (<>
                  All plans have our <span className='font-medium bg-[linear-gradient(90deg,transparent_0%,#ffdc59)] py-1 pr-2 leading-normal sm:leading-[41px] saturate-[1.2] transition-all duration-200 ease-in-out hover:bg-[linear-gradient(90deg,transparent_-20%,#ffdc59)] hover:text-black hover:saturate-[2] pl-0'>
                  100% growth guarantee</span>
                <br />
                You can <span className="font-medium">cancel for free at anytime</span>.

                {/* TODO: IG Account Preview */}

              </>)
              : (<>
                  <span className='block py-1 text-gray-400 text-md leading-[26px] lg:leading-[22px]'>
                    Sign up & start your TikTok growth journey.{" "}
                  <br className='hidden sm:block' />
                  You'll also receive our{" "}
                    <span className='font-medium text-gray-500 bg-[linear-gradient(90deg,transparent_0%,#ffdc59)] py-1 pr-2 leading-normal sm:leading-[41px] saturate-[1.2] transition-all duration-200 ease-in-out hover:bg-[linear-gradient(90deg,transparent_-20%,#ffdc59)] hover:text-black hover:saturate-[2] pl-0'>
                    100% growth guarantee
                  </span>
                </span>
              </>)
            )}
          </p>
        </div>

          <label className="block font-medium text-gray-700 text-sm mb-1.5 mt-5" htmlFor="email">Email</label>
        <div className="relative mt-1">
          <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
              <svg className="absolute w-[18px] h-[18px] text-neutral-400 transform -translate-y-1/2 left-3 top-1/2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
            <input className="w-full h-10 px-3 pl-[39px] text-sm border rounded-lg border-neutral-200 hover:border-neutral-300 focus:border-turquoise focus:shadow focus:ring-0 focus:shadow-turquoise-lighter focus:outline-none placeholder-neutral-500"
            id="email"
            name="email"
            type="email"
            placeholder="Enter your email"
            disabled={isLoading}
            onChange={(event) => {
              setSetupStates(prevState => ({ ...prevState, email: event.target.value }))
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter')
                setSearchParams({ step: '2' })
            }}
            required
            autoComplete={'email'}
            autoCapitalize="off"
            defaultValue={setupStates.email}
          />
        </div>
          <label className="block font-medium text-gray-700 text-sm mb-1.5 mt-5" htmlFor="password">Password</label>
          <div className="relative mt-1 mb-2">
          <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
              <svg className="absolute w-[18px] h-[18px] text-gray-400 transform -translate-y-1/2 left-3 top-1/2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
            <input className="w-full h-10 px-3 pl-[39px] text-sm border rounded-lg border-neutral-200 hover:border-neutral-300 focus:border-turquoise focus:shadow focus:ring-0 focus:shadow-turquoise-lighter focus:outline-none placeholder-neutral-500 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100"
            id="password"
            name="password"
            type="password"
            autoComplete={"password"}
            placeholder="Enter your password"
            disabled={isLoading}
            onChange={(event) => {
              setSetupStates(prevState => ({ ...prevState, password: event.target.value }))
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter')
                setSearchParams({ step: '2' })
            }}
            required
            autoCapitalize="off"
          />
        </div>
        {isLoading ? (
          <Button type="submit" disabled className="!mt-5 flex items-center justify-center w-full px-4 py-3.5 text-md font-medium text-black bg-yellow-300 border border-transparent shadow-sm cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2">
            <LoadingSpinner className="w-4 h-4 mr-1" />
            Activating account...
          </Button>
        ) : (
              <Button type="submit" className={classNames("!mt-7 flex justify-center w-full px-4 py-3.5 text-md font-medium text-white border border-transparent shadow-md hover:shadow-lg focus:outline-none focus:ring-2 relative overflow-hidden",
            accountColor.bgColor, accountColor.hoverColor, accountColor.ringColor
          )}
            onClick={() => {
              setSearchParams({ step: '2' })
              // Track event
              track('Signed Up', {
                username: setupStates.tiktokUsername,
                email: setupStates.email,
              })
            }}>
            Activate your account
            <HiArrowSmRight className="w-5 h-5 place-self-center" />
          </Button>
        )}

        <span className="inline-flex items-center w-full mt-5 font-medium tracking-tight text-green-500 align-middle md:mt-6 place-content-center">
          <LockClosedIcon className="mr-1 h-[0.85rem] w-[0.85rem]" />
          <span>SSL Encrypted</span>
        </span>

        {forceSignupCaptcha && (
          <div className="flex justify-center mt-4">
            <div className="cf-turnstile checkbox" id="cf-turnstile"
              data-sitekey={process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY}
              data-theme="light"
            // data-appearance="interaction-only"
            />
          </div>
        )}
      </div>
    )
  ), [router, setupStates.activePeriod, setupStates.tiktokUsername, setupStates.email, setupStates.password, accountColor, isLoading, step]);


  const Step3 = cbInstance ? (
    <div className='relative animate-fade-in-up-bounce'>
      <div className="space-y-3 text-center">

        {!isLoggedInSetup && (
          <Link href="#" title="Back" className="flex items-center gap-2 pb-2 group"
            onClick={(e) => {
              e.preventDefault()
              setSearchParams({ step: (step - 1).toString() })
            }}
          >
            <FiArrowLeft className="w-5 h-5 text-neutral-400 group-hover:text-neutral-500" />
            <span className="text-neutral-400 group-hover:text-neutral-500">Back</span>
          </Link>
        )}

        <h1 className={`mt-10 mb-2 text-3xl font-bold text-neutral-900 sm:text-3xl lg:mt-5 xl:mt-5 xl:text-4xl`}>
          {isLoggedInSetup && (
            <div className="flex flex-col items-center justify-center">
              <LightningFillIcon className="inline w-8 h-8 mb-2 text-orange-500 align-middle" />
              {/* Start your Insta growth */}
              {/* Activate your growth */}
              Guaranteed growth <br />(or it's 100% free!)
            </div>
          )}
        </h1>
        <p className={classNames("mx-auto text-md text-gray-400", isLoggedInSetup ? "max-w-sm" : "max-w-md")}>
          {isLoggedInSetup && (
            <>
              {/* Almost there! <br /> */}
              {/* You will <span className='font-semibold'>not</span> be charged today. <br />
              You can cancel your trial <span className='font-semibold'>for free</span> at anytime. */}
              Start your TikTok growth for <span className="font-semibold">free</span>.
              <br />
              No contracts. No gimmicks. Cancel anytime.
            </>
          )}
        </p>
      </div>

      <ChargebeePaymentForm
        selectedPlan={setupStates.selectedPlan}
        selectedYearly={setupStates.activePeriod == "Yearly"}
        selectedUsername={setupStates.tiktokUsername}
        accountColor={accountColor.name}
        isFromSetup={true}
        userCountry={userCountry}
      />
    </div>
  ) : null;

  return (
    <div className={classNames("container block items-center h-full mx-auto overflow-auto overflow-visible text-left",
      isLoggedInSetup && 'px-5',
      step == 0 ? (isLoggedInSetup ? 'max-w-md' : 'max-w-sm') : step == 2 ? 'max-w-lg' : 'max-w-7xl'
    )}>
      <div className={classNames(
        "fixed left-0 top-0 w-full h-[8px] z-50",
        "bg-gray-200 md:bg-inherit md:bg-gradient-to-r from-gray-200 via-[var(--color-gray-200)_85%] to-white"
      )}>
        <div
          className="h-full transition-all duration-1000 ease-in-out bg-purple-600 rounded-tr-full rounded-br-full saturate-150"
          style={{ width: progressWidth }}
        />
      </div>
      {/* <div className='items-center mx-auto text-center'>
        <LoadingSpinner className="w-8 h-8 !mx-auto text-purple-500" />
        <p className="mt-3 font-medium text-purple-500 text-md">Logging you in ...</p>
      </div> */}

      {/* Debugging */}
      {/* <div className='absolute bottom-0 right-0 p-4 bg-blue-100 rounded-md shadow-lg w-[600px] z-10'>
        <h2 className='text-lg font-bold text-blue-800'>Debugging Information:</h2>
        <ul className='mt-2 text-blue-700 list-disc list-inside'>
          <li><b>Current Step:</b> {step}</li>
          <li><b>TikTok Username:</b> {setupStates.instagramUsername}</li>
          <li><b>Selected Period:</b> {JSON.stringify(activePeriod)}</li>
          <li><b>Selected Plan:</b> {JSON.stringify(selectedPlan)}</li>
        </ul>
      </div> */}


      <div className="mt-10 lg:my-16">

        {errorMessage && (
          <div className={`animate-fade-in-up-short relative w-full min-w-full mb-5 px-4 py-3 pr-6 text-white bg-red-500 text-sm rounded-xl ${!isLoggedInSetup && 'max-w-sm'}`} role="alert">
            <span className="block sm:inline" dangerouslySetInnerHTML={{ __html: errorMessage }}></span>
            <span className="absolute top-0 bottom-0 right-0 self-center px-4">
              <svg className="w-6 h-6 text-white fill-current" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" onClick={() => setErrorMessage(null)}><title>Close</title><path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"></path></svg>
            </span>
          </div>
        )}

        {/* Debugging */}

        {/* User: {JSON.stringify(user)}<br />
        Step: {step}<br />
        Email: {setupStates.email}<br />
        Password: {setupStates.password}<br />
        isLoggedInSetup: {isLoggedInSetup ? 'true' : 'false'}<br />
        Selected username: {setupStates.tiktokUsername}<br />
        Selected period: {JSON.stringify(setupStates.activePeriod)}<br />
        Selected plan: {JSON.stringify(setupStates.selectedPlan)}<br />
        Selected color: {JSON.stringify(accountColor)}<br /> */}


        {/* TODO: Uncomment this to enable step transitions */}
        <SetupLayoutContext.Provider value={{ tiktokUsername: setupStates.tiktokUsername, accountColor, activePeriod: setupStates.activePeriod, setActivePeriod: handlePeriodSelected, selectedPlan: setupStates.selectedPlan, setSelectedPlan: handlePlanSelected, isLoading, setIsLoading, setErrorMessage }}>
          {/* <AnimatePresence mode='wait'>
            {step === 0 && (
              <motion.div key="step1" variants={variants} initial="initial" animate="enter" exit="exit">{Step1}</motion.div>
            )}
            {step === 1 && (
              <motion.div key="step2" variants={variants} initial="initial" animate="enter" exit="exit">{Step2}</motion.div>
            )}
            {step === 2 && (
              <motion.div key="step3" variants={variants} initial="initial" animate="enter" exit="exit">{Step3}</motion.div>
            )}
          </AnimatePresence> */}
          {step === 0 && Step1}
          {step === 1 && Step2}
          {step === 2 && Step3}
        </SetupLayoutContext.Provider>

        {/* Load Chargebee.js */}
        <Script src="https://js.chargebee.com/v2/chargebee.js" strategy="afterInteractive"
          onLoad={() => { console.log("CB has loaded") }}
        />
        {/* <Script id="chargebee-config" strategy="afterInteractive">
          {`
              var waitForChargebee = setInterval(function () {
                if (typeof Chargebee != 'undefined') { 
                  window.chargebeeInstance = Chargebee.init({
                    site: "${chargebeeSite}",
                    publishableKey: "${chargebeeFrontendSettingsForSite(chargebeeSite).publishableKey}",
                    iFrameOnly: true,
                  });
                  window.Chargebee = Chargebee;
                  clearInterval(waitForChargebee);
                }
              }, 50);
            `}
        </Script> */}

      </div>

      {forceSignupCaptcha && (
        <Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" async={true} defer={true} />
      )}
    </div>
  );
}
